import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DropdownMenuData, DropdownMenuItem, DropdownModule } from "@vg-constellation/angular-17/dropdown";
import { InputComponent } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";
import { AccountOwnerStrategy, TrustOwnerData } from "../account-owner-strategy.interface";
import { AccountOwnerType } from "../../../../types/AccountOwnerType";
import { AccountType } from "../../../../types/AccountType";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import { Trust } from "@models/estateProfile.model";
import {
  ACCOUNT_DETAILS_ACCOUNT_NAME_SUB_TEXT,
  ACCOUNT_DETAILS_REQUIRED_FIELDS,
  ACCOUNT_DETAILS_TRUST_NAME,
  TRUST_DETAILS_TRUST_DISPLAY_NAME,
  TRUST_DETAILS_DISPLAY_NAME_DESC,
  TRUST_DETAILS_LEGAL_NAME_DESC,
  TRUST_DETAILS_SETTLOR_DESC
} from "@services/content/guided-flow/account-details-content";
import { EditFlowService } from "@services/edit-flow/edit-flow.service";
import {HintErrorComponent} from "@vg-constellation/angular-17/hint-error";
import { WebLogger } from "@weps/shared-http";
import { DropdownTypeaheadDirective } from "@weps/shared-ui";
import { OwnerType } from "../../../../types/OwnerType";

/**
 * Trust Owner Strategy Component
 *
 * Handles owner selection for trust accounts.
 * Implements AccountOwnerStrategy interface.
 *
 * Responsibilities:
 * - Generate dropdown options from existing trusts in estate profile
 * - Handle trust selection changes
 * - Validate trust selection
 * - Provide trust data in standardized format
 * - Support edit mode with pre-selected trust
 *
 * Note: This component handles selection of EXISTING trusts only.
 * Creating new trusts is handled by a separate flow (trust-details component).
 */
@Component({
  selector: 'app-trust-owner',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownModule, InputComponent, LabelComponent, HintErrorComponent, DropdownTypeaheadDirective],
  templateUrl: './trust-owner.component.html',
  styleUrls: ['./trust-owner.component.scss'],
})
export class TrustOwnerComponent implements OnInit, AccountOwnerStrategy {
  readonly globalStore = inject(GlobalStateStore);
  readonly editFlowService = inject(EditFlowService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly ownerType = AccountOwnerType.TRUST;
  readonly ACCOUNT_DETAILS_REQUIRED_FIELDS = ACCOUNT_DETAILS_REQUIRED_FIELDS;
  readonly ACCOUNT_DETAILS_TRUST_NAME = ACCOUNT_DETAILS_TRUST_NAME;
  readonly TRUST_DETAILS_TRUST_DISPLAY_NAME = TRUST_DETAILS_TRUST_DISPLAY_NAME;
  readonly TRUST_DETAILS_DISPLAY_NAME_DESC = TRUST_DETAILS_DISPLAY_NAME_DESC;
  readonly TRUST_DETAILS_LEGAL_NAME_DESC = TRUST_DETAILS_LEGAL_NAME_DESC;
  readonly TRUST_DETAILS_SETTLOR_DESC = TRUST_DETAILS_SETTLOR_DESC;

  // Inputs
  @Input() accountType: AccountType;
  @Input() isEditMode: boolean = false;

  // Outputs
  @Output() ownerChange = new EventEmitter<void>();
  @Output() strategyReady = new EventEmitter<AccountOwnerStrategy>();

  // Data binding
  selectedTrustId: string | null = null;
  trustOptions: DropdownMenuData = { groups: [] };
  validationError: string | null = null;

  // Field validation states
  validSelectedTrustId: boolean = true
  validTrustLegalNameInput: boolean = true;
  validTrustDisplayNameInput: boolean = true;
  validTrustTypeInput: boolean = true;
  validSettlorInput: boolean = true;

  // Trust detail fields
  settlorOptions: DropdownMenuData = { groups: [] };
  selectedSettlor: string = '';
  trustTypeOptions: DropdownMenuData = { groups: [] };
  selectedTrustType: string = '';
  trustLegalName: string = '';
  trustDisplayName: string = '';

  // Cached data
  private availableTrusts: Trust[] = [];
  selectedTrust: Trust | null = null;
  fromVgTable: boolean = false;

  ngOnInit(): void {
    // Set fromVgTable based on tableType from guided flow state
    this.fromVgTable = this.globalStore.isVanguard(this.accountType);

    // Initialize dropdown options to prevent undefined errors
    this.initializeDropdownOptions();

    // Load available trusts from estate profile
    this.loadTrustsFromEstateProfile();

    // Generate trust options
    this.trustOptions = this.getOwnerOptions();

    // Register this strategy with parent container
    this.strategyReady.emit(this);
  }

  /**
   * Initialize dropdown options to prevent undefined errors
   */
  private initializeDropdownOptions(): void {
    this.settlorOptions = { groups: [{ items: [] }] };
    this.trustTypeOptions = { groups: [{ items: [] }] };
  }

  /**
   * Loads available trusts from estate profile
   */
  private loadTrustsFromEstateProfile(): void {
    const estateProfile = this.globalStore.estateProfile?.();
    if (!estateProfile) {
      WebLogger.log({ message: "TrustOwner estate profile not available", level: "warn" });
      return;
    }

    this.availableTrusts = estateProfile.trusts || [];
  }

  /**
   * Generates dropdown options for trust selection
   * Implements AccountOwnerStrategy.getOwnerOptions()
   */
  getOwnerOptions(): DropdownMenuData {
    const items: DropdownMenuItem[] = [];

    // Add "+ Add new trust" option first
    items.push({
      labelText: '+ Add new trust',
      id: 'ADD_NEW_TRUST',
    });

    if (this.availableTrusts.length === 0) {
      WebLogger.log({ message: "TrustOwner no trusts available for selection", level: "warn" });
      return {
        groups: [
          {
            items: items,
          },
        ],
      };
    }

    // Create dropdown item for each trust
    this.availableTrusts.forEach((trust) => {
      //TODO: This used to fallback to the providedBy POID. Why?
      // Use clientPoid as primary identifier
      const trustId = trust.referenceIds?.clientPoid;
      const trustUuid = trust.householdMemberId;

      // Determine label based on settlor
      const trustLabel = trust.displayName || trust.legalName || 'Unnamed Trust';

      if (trustId) {
        items.push({
          labelText: trustLabel,
          id: trustId,
        });
        return; // Vanguard Trust, return early
      }

      if (trustUuid) {
        items.push({
          labelText: trustLabel,
          id: trustUuid,
        });
      }
    });

    return {
      groups: [
        {
          items: items,
        },
      ],
    };
  }

  /**
   * Handles dropdown selection change
   */
  onTrustSelectionChange(event: any): void {
    this.selectedTrustId = event.activeItems[0];

    // Handle "+ Add new trust" selection
    if (this.selectedTrustId === 'ADD_NEW_TRUST') {
      // TODO: Navigate to trust creation flow
      // Set adding new trust mode and clear fields
      this.selectedTrust = null;
      this.trustLegalName = '';
      this.trustDisplayName = '';
      this.selectedTrustType = '';
      this.selectedSettlor = '';
      this.validationError = null;

      // Rebuild options to reset dropdowns
      this.buildSettlorOptions();
      this.buildTrustTypeOptions();

      this.ownerChange.emit();
      return;
    }

    // Find the selected trust
    if (this.selectedTrustId) {
      // Clear add new trust mode when selecting existing trust
      this.selectedTrust = this.findTrustById(this.selectedTrustId);

      // Populate fields from selected trust
      if (this.selectedTrust) {
        this.trustLegalName = this.selectedTrust.legalName || '';
        this.trustDisplayName = this.selectedTrust.displayName || '';
        this.selectedTrustType = this.selectedTrust.trustType || '';

        // Build settlor and trust type options
        this.buildSettlorOptions();
        this.buildTrustTypeOptions();

        // Set initial settlor based on trust
        this.selectedSettlor = this.getSettlorEnumValue();
      }
    }

    // Clear validation error on change
    this.validationError = null;

    // Save selected trust to guided flow state for edit mode
    if (this.selectedTrustId) {
      this.globalStore.setGuidedFlowInitialState('trust', {
        currentAccountOwner: this.selectedTrustId,
      });
    }

    // Trigger change detection to update UI immediately
    this.cdr.detectChanges();

    // Notify parent container
    this.ownerChange.emit();
  }

  /**
   * Handles settlor dropdown change
   */
  onSettlorChange(event: any): void {
    this.selectedSettlor = event.activeItems[0];
    this.validSettlorInput = !!this.selectedSettlor;
    this.ownerChange.emit();
  }

  /**
   * Handles trust type dropdown change
   */
  onTrustTypeChange(event: any): void {
    this.selectedTrustType = event.activeItems[0];
    this.validTrustTypeInput = !!this.selectedTrustType;

    // Clear Joint settlor selection if switching to Irrevocable trust
    // This prevents invalid state where Joint is selected for Irrevocable trusts
    if (this.selectedTrustType === 'IRREVOCABLE' && this.selectedSettlor === 'JOINT') {
      this.selectedSettlor = '';
    }

    // Rebuild settlor options as Joint is not available for Irrevocable
    this.buildSettlorOptions();

    this.ownerChange.emit();
  }

  /**
   * Handles trust display name input change
   */
  onTrustDisplayNameChange(event: any): void {
    this.trustDisplayName = event.target.value;
    this.validTrustDisplayNameInput = !!this.trustDisplayName;

    this.ownerChange.emit();
  }

  /**
   * Handles trust legal name input change
   */
  onTrustLegalNameChange(event: any): void {
    this.trustLegalName = event.target.value;
    this.validTrustLegalNameInput = !!this.trustLegalName;

    this.ownerChange.emit();
  }

  /**
   * Builds settlor dropdown options
   */
  private buildSettlorOptions(): void {
    const estateProfile = this.globalStore.estateProfile?.();
    const items: DropdownMenuItem[] = [];

    if (estateProfile) {
      const primaryClient = estateProfile.primaryClient;
      const spouse = this.globalStore.findSpouseData();

      // Add Primary Client option
      if (primaryClient) {
        items.push({
          labelText: `${primaryClient.firstName} ${primaryClient.lastName}`,
          id: 'PRIMARY',
        });
      }

      // Add Spouse option
      if (spouse) {
        items.push({
          labelText: `${spouse.firstName} ${spouse.lastName}`,
          id: 'SECONDARY',
        });
      }

      // Add Joint option only for Revocable trusts or when no trust type selected
      const shouldShowJoint = primaryClient && spouse && this.selectedTrustType !== 'IRREVOCABLE';

      if (shouldShowJoint) {
        items.push({
          labelText: 'Joint',
          id: 'JOINT',
        });
      }
    }

    // Always set a valid structure, even if empty
    this.settlorOptions = {
      groups: [
        {
          items: items,
        },
      ],
    };
  }

  /**
   * Builds trust type dropdown options
   */
  private buildTrustTypeOptions(): void {
    this.trustTypeOptions = {
      groups: [
        {
          items: [
            { labelText: 'Revocable', id: 'REVOCABLE' },
            { labelText: 'Irrevocable', id: 'IRREVOCABLE' },
          ],
        },
      ],
    };
  }

  /**
   * Finds a trust by its ID (clientPoid or providedBy)
   */
  private findTrustById(trustId: string): Trust | null {
    return (
      this.availableTrusts.find(
        (trust) =>
          trust.householdMemberId === trustId ||
          trust.referenceIds?.clientPoid === trustId ||
          trust.providedBy === trustId,
      ) || null
    );
  }

  /**
   * Validates current trust selection
   * Implements AccountOwnerStrategy.validateOwnerSelection()
   */
  validateOwnerSelection(): boolean {
    return this.hasValidTrustData();
  }

  /**
   * Comprehensive validation for all trust fields
   * Returns false if there are validation errors
   */
  private hasValidTrustData(): boolean {
    //Validate current inputs
    const validation = this.validateTrustInputs(
      this.selectedTrustId,
      this.trustLegalName,
      this.trustDisplayName,
      this.selectedTrustType,
      this.selectedSettlor,
    );

    //Update individual 'valid' properties
    this.validSelectedTrustId = validation.validTrustId
    this.validTrustLegalNameInput = validation.validLegalName;
    this.validTrustDisplayNameInput = validation.validTrustDisplayName;
    this.validTrustTypeInput = validation.validTrustType;
    this.validSettlorInput = validation.validSettlor;

    return (
      this.validSelectedTrustId &&
      this.validTrustLegalNameInput &&
      this.validTrustDisplayNameInput &&
      this.validTrustTypeInput &&
      this.validSettlorInput
    );
  }

  /**
   * Validates trust-specific inputs
   */
  validateTrustInputs(
    trustId: string | null,
    trustLegalName: string,
    trustDisplayName: string,
    trustType: string,
    trustSettlor: string,
  ): any {
    return {
      validTrustId: !!trustId,
      validLegalName: !!trustLegalName,
      validTrustDisplayName: !!trustDisplayName,
      validTrustType: !!trustType && trustType !== 'UNKNOWN',
      validSettlor: !!trustSettlor,
    };
  }

  /**
   * Gets validation error message
   * Implements AccountOwnerStrategy.getValidationError()
   */
  getValidationError(): string | null {
    return this.validationError;
  }

  /**
   * Gets the currently selected trust ID
   * Implements AccountOwnerStrategy.getSelectedOwner()
   */
  getSelectedOwner(): string | null {
    return this.selectedTrustId;
  }

  /**
   * Sets the selected trust (for edit mode)
   * Implements AccountOwnerStrategy.setSelectedOwner()
   */
  setSelectedOwner(trustId: string): void {
    this.selectedTrust = this.findTrustById(trustId);

    // Find the selected trust and set the correct dropdown ID
    if (this.selectedTrust) {
      const vanguardTrustId = this.selectedTrust.referenceIds?.clientPoid;
      if (vanguardTrustId) {
        this.selectedTrustId = vanguardTrustId;
      } else {
        this.selectedTrustId = this.selectedTrust.householdMemberId || null;
      }

      // Clear add new trust mode when selecting existing trust

      this.trustLegalName = this.selectedTrust.legalName || '';
      this.trustDisplayName = this.selectedTrust.displayName || '';
      this.selectedTrustType = this.selectedTrust.trustType || '';

      this.buildTrustTypeOptions();
      this.buildSettlorOptions();

      // Set settlor BEFORE EditFlowService call
      this.selectedSettlor = this.getSettlorEnumValue();

      // If in edit mode, populate fields using EditFlowService
      if (this.isEditMode) {
        this.populateFieldsFromEditFlow();
      }
    } else {
      WebLogger.log({ message: "TrustOwner trust not found for ID", level: "warn", payload: { selectedTrustId: this.selectedTrustId } });
    }

    // ALWAYS trigger change detection after setting selectedTrustId (not just in edit mode)
    this.cdr.detectChanges();
  }

  /**
   * Clears the current selection
   * Implements AccountOwnerStrategy.clearSelection()
   */
  clearSelection(): void {
    this.selectedTrustId = null;
    this.selectedTrust = null;
    this.validationError = null;
  }

  /**
   * Gets trust owner data in standardized format
   * Implements AccountOwnerStrategy.getOwnerData()
   */
  getOwnerData(): TrustOwnerData | null {
    if (!this.selectedTrust) {
      return null;
    }

    const ownerData: TrustOwnerData = {
      ownerType: AccountOwnerType.TRUST,
      ownerId: 'TRUST_OWNER',
      ownerUuid: this.selectedTrust.householdMemberId,
      trustId: this.selectedTrustId,
      trustLegalName: this.trustLegalName || this.selectedTrust.legalName || '',
      trustDisplayName: this.trustDisplayName || this.selectedTrust.displayName || '',
      trustType: this.selectedTrustType || this.selectedTrust.trustType || 'UNKNOWN',
      settlor: this.selectedSettlor || this.getSettlorEnumValue(),
      trustPoid: this.selectedTrust.referenceIds?.clientPoid,
      isEdit: this.isEditMode,
      jointType: null,
    };

    return ownerData;
  }

  /**
   * Determines settlor enum value from trust settlors
   */
  private getSettlorEnumValue(): string {
    if (
      !this.selectedTrust ||
      !this.selectedTrust.settlors ||
      this.selectedTrust.settlors.length === 0
    ) {
      return '';
    }

    const estateProfile = this.globalStore.estateProfile?.();
    if (!estateProfile) {
      return '';
    }

    const primaryClient = estateProfile.primaryClient;
    const spouse = this.globalStore.findSpouseData();

    // If 2 settlors, check if it's joint (primary + spouse)
    if (this.selectedTrust.settlors.length === 2) {
      const settlorIds = this.selectedTrust.settlors.map((s) => s.householdMemberId);
      const hasPrimaryClient =
        primaryClient && primaryClient.uuid && settlorIds.includes(primaryClient.uuid);
      const hasSpouse = spouse && spouse.uuid && settlorIds.includes(spouse.uuid);

      if (hasPrimaryClient && hasSpouse) {
        return 'JOINT';
      }
    }

    // Single settlor - determine if PRIMARY or SPOUSE
    const settlor = this.selectedTrust.settlors[0];
    const settlorId = settlor.householdMemberId;

    if (primaryClient && settlorId === primaryClient.uuid) {
      return 'PRIMARY'; // Primary wealth holder
    } else if (spouse && settlorId === spouse.uuid) {
      return 'SECONDARY';
    }

    return '';
  }

  /**
   * Gets display label for current selection
   */
  getSelectedTrustLabel(): string {
    if (!this.selectedTrustId) {
      return '';
    }

    const allItems = this.trustOptions.groups[0]?.items || [];
    const selectedItem = allItems.find((item) => item.id === this.selectedTrustId);
    return selectedItem?.labelText || '';
  }

  /**
   * Gets the count of available trusts
   */
  getAvailableTrustCount(): number {
    return this.availableTrusts.length;
  }

  /**
   * Handles trust-specific submission logic
   * @param accountType The account type being submitted
   * @param isEditMode Whether this is an edit operation
   * @param globalStore Reference to global state store
   * @returns Object containing trust submission data or null if validation fails
   */
  onSubmit(accountType: string, isEditMode: boolean, globalStore: any): any {
    const fromVgTable = globalStore.isVanguard(accountType);
    const isVgTrust = !!this.selectedTrust?.referenceIds?.clientPoid
    const trustOwnerData = this.getOwnerData() as any;

    const isUpdate = trustOwnerData?.ownerUuid && trustOwnerData?.ownerUuid.trim() !== '';
    // Create the UUID from the frontend if we're creating manually entered trusts so that we can create assets owned by the trust
    const uuid: string | null = !isUpdate ? crypto.randomUUID() : null

    // Determine the appropriate owner UUID based on trust type
    let ownerUuid: string | null | undefined = null;
    let householdMemberId: string | null = null;

    //TODO: Simplify. Utter Garbage.
    if (isUpdate) {
      if (isVgTrust) {
        // For Vanguard trusts, only send householdMemberId if the trust actually has one
        ownerUuid = this.selectedTrust?.householdMemberId;
      } else {
        // For non-Vanguard trusts, the selectedTrustId is the householdMemberId
        ownerUuid = this.selectedTrustId;
        householdMemberId = this.selectedTrustId; // Not needed, duplicate
      }
    } else {
      // New trust being created
      ownerUuid = uuid;
      householdMemberId = isVgTrust ? null : uuid; //Not needed, duplicate
    }

    // Create trust data for submission using input fields
    const trustData: any = {
      ownerUuid: ownerUuid,
      trustLegalName: this.trustLegalName,
      trustDisplayName: this.trustDisplayName,
      trustType: this.selectedTrustType,
      settlor: this.selectedSettlor,
      trustPoid: this.selectedTrust?.referenceIds?.clientPoid || null,
      isEdit: this.isEditMode, //This is for rendering and prepopulating edit fields
      isUpdate: isUpdate, //For payload building
      isVgTrust: isVgTrust,
      ownerType: OwnerType.TRUST_OWNER
    };

    //TODO: Because Vanguard Trusts are currently treated as account in the saving part, this is needed for now.
    // Add accountType for TRUST account type
    if (accountType === 'trust') {
      trustData['accountType'] = 'TRUST';
    }

    // Update the global state with trust data
    globalStore.updateGuidedFlowTempState('owner-type', trustData);
    globalStore.mergeGuidedFlowTempState('owner-type');

    return trustData;
  }

  /**
   * Checks if any trusts are available
   */
  hasTrustsAvailable(): boolean {
    return this.availableTrusts.length > 0;
  }

  isVanguardTrust(): boolean {
    if (!this.selectedTrust) {
      return false;
    }
    return !!this.selectedTrust.referenceIds?.clientPoid;
  }

  // EditFlowService-compatible handlers
  trustLegalNameInputChangeHandler(input: string): void {
    this.trustLegalName = input;
  }

  trustDisplayNameInputChangeHandler(input: string): void {
    this.trustDisplayName = input;
  }

  trustTypeDropdownChangeHandler(event: { activeItems: string[] }): void {
    this.selectedTrustType = event.activeItems[0];

    // Rebuild settlor options as Joint is not available for Irrevocable
    this.buildSettlorOptions();
  }

  trustSettlorDropdownChangeHandler(event: { activeItems: string[] }): void {
    this.selectedSettlor = event.activeItems[0];
  }

  /**
   * Populates form fields using EditFlowService during edit mode
   */
  private populateFieldsFromEditFlow(): void {
    const context = {
      accountType: this.accountType,
      showSubAccountType: false,
    };

    const handlers = {
      // Required handlers (not used for trust but required by interface)
      ownerSelectorDropdownChangeHandler: () => {},
      nameInputChangeHandler: () => {},
      balanceInputChangeHandler: () => {},
      trustLegalNameInputChangeHandler: this.trustLegalNameInputChangeHandler.bind(this),
      trustDisplayNameInputChangeHandler: this.trustDisplayNameInputChangeHandler.bind(this),
      trustTypeDropdownChangeHandler: this.trustTypeDropdownChangeHandler.bind(this),
      trustSettlorDropdownChangeHandler: this.trustSettlorDropdownChangeHandler.bind(this),
    };

    // Call EditFlowService to populate fields
    this.editFlowService.setEditFields(context, handlers);
  }
}
